import React, { useState } from 'react';
import { Menu, Search, Bell, MessageSquare, Settings, User, Mail, Lock, LogOut, X, ChevronDown, ChevronRight, Users, Briefcase, Activity, Clock, TrendingUp, Package, Eye, DollarSign, Rocket, LayoutDashboard, FileText, BarChart3, Home } from 'lucide-react';

const AdminDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showContactModal, setShowContactModal] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [activeMenu, setActiveMenu] = useState('dashboard');

  const [contactData, setContactData] = useState({
    email: "",
    contact: "",
    address: "",
    OpenHour: "",
    LocationMap: "",
    SocialMedia: {
      Facebook: "",
      Instagram: "",
      Linkedin: "",
      GitHub: ""
    }
  });

  const handleChange = (e) => {
    setContactData(prev =>
      ["Facebook", "Instagram", "Linkedin", "GitHub"].includes(e.target.name) ?
        { ...prev, SocialMedia: { ...prev.SocialMedia, [e.target.name]: e.target.value } } :
        { ...prev, [e.target.name]: e.target.value }
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    closeModal();
  };

  const openModal = () => {
    setShowContactModal(true);
    setIsClosing(false);
  };

  const closeModal = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShowContactModal(false);
      setIsClosing(false);
    }, 300);
  };

  const statsCards = [
    { icon: Users, title: "Total Team", count: "4", subtitle: "members", color: "from-blue-500 to-blue-600" },
    { icon: Menu, title: "Navigation Items", count: "7", subtitle: "items", color: "from-purple-500 to-purple-600" },
    { icon: Mail, title: "Contact Requests", count: "40", subtitle: "people", color: "from-green-500 to-green-600" },
    { icon: Briefcase, title: "Total Jobs", count: "6", subtitle: "positions", color: "from-orange-500 to-orange-600" }
  ];

  const recentActivities = [
    { icon: Users, title: "New team member added", time: "2 hours ago", color: "blue" },
    { icon: Mail, title: "New person get in touch", time: "3 hours ago", color: "green" },
    { icon: Briefcase, title: "Job posting updated", time: "5 hours ago", color: "orange" },
    { icon: Activity, title: "System backup completed", time: "8 hours ago", color: "purple" }
  ];

  const menuItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { id: 'team', icon: Users, label: 'Manage Team' },
    { id: 'navigation', icon: Menu, label: 'Navigation' },
    { id: 'contact', icon: Mail, label: 'Contact Requests' },
    { id: 'jobs', icon: Briefcase, label: 'Job Postings' },
    { id: 'analytics', icon: BarChart3, label: 'Analytics' },
    { id: 'settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">Welcome back! Here's what's happening today.</p>
        </div>
        <button
          onClick={openModal}
          className="px-4 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:shadow-lg transition-all duration-200 text-sm font-medium flex items-center gap-2 justify-center"
        >
          <span className="text-lg">+</span>
          Update Contact Info
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statsCards.map((card, idx) => {
          const Icon = card.icon;
          return (
            <div key={idx} className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 group">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm text-gray-500 font-medium">{card.title}</p>
                  <div className="flex items-baseline gap-2 mt-2">
                    <h3 className="text-3xl font-bold text-gray-900 group-hover:text-primary transition-colors">{card.count}</h3>
                    <span className="text-sm text-gray-400">{card.subtitle}</span>
                  </div>
                </div>
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${card.color} flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-5 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="bg-blue-50 p-2 rounded-lg">
              <Activity className="text-blue-600" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Recent Activity</h2>
              <p className="text-gray-500 text-sm">Latest updates from your dashboard</p>
            </div>
          </div>
          <button className="text-blue-600 hover:text-blue-700 font-medium text-sm self-start sm:self-auto hover:underline">
            View All
          </button>
        </div>

        <div className="divide-y divide-gray-100">
          {recentActivities.map((activity, index) => {
            const Icon = activity.icon;
            const colorMap = {
              blue: 'bg-blue-50 text-blue-600',
              green: 'bg-green-50 text-green-600',
              orange: 'bg-orange-50 text-orange-600',
              purple: 'bg-purple-50 text-purple-600'
            };
            return (
              <div key={index} className="p-5 hover:bg-gray-50 transition-colors duration-150 flex items-center gap-4">
                <div className={`${colorMap[activity.color]} p-3 rounded-lg flex-shrink-0`}>
                  <Icon size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-gray-900 font-medium truncate">{activity.title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Clock size={14} className="text-gray-400 flex-shrink-0" />
                    <p className="text-gray-500 text-sm">{activity.time}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Contact Modal */}
      {showContactModal && (
        <div className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 transition-opacity duration-300 ${isClosing ? 'opacity-0' : 'opacity-100'}`}>
          <div className={`bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto transition-transform duration-300 ${isClosing ? 'scale-95' : 'scale-100'}`}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center rounded-t-xl z-10">
              <h3 className="text-xl font-bold text-gray-900">Update Contact Details</h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
                    <input
                      onChange={handleChange}
                      value={contactData.email}
                      name="email"
                      type="email"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm outline-none"
                      placeholder="contact@company.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Contact Number</label>
                    <input
                      onChange={handleChange}
                      value={contactData.contact}
                      name="contact"
                      type="text"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm outline-none"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Address</label>
                  <input
                    onChange={handleChange}
                    value={contactData.address}
                    name="address"
                    type="text"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm outline-none"
                    placeholder="123 Business St, City, State"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Opening Hours</label>
                    <input
                      onChange={handleChange}
                      value={contactData.OpenHour}
                      name="OpenHour"
                      type="text"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm outline-none"
                      placeholder="Mon-Fri: 9AM - 6PM"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Location Map Link</label>
                    <input
                      onChange={handleChange}
                      value={contactData.LocationMap}
                      name="LocationMap"
                      type="url"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm outline-none"
                      placeholder="https://maps.google.com/..."
                    />
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <h4 className="text-base font-bold text-gray-900 mb-4">Social Media Links</h4>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Facebook</label>
                      <input
                        onChange={handleChange}
                        value={contactData.SocialMedia.Facebook}
                        name="Facebook"
                        type="url"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm outline-none"
                        placeholder="https://facebook.com/..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Instagram</label>
                      <input
                        onChange={handleChange}
                        value={contactData.SocialMedia.Instagram}
                        name="Instagram"
                        type="url"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm outline-none"
                        placeholder="https://instagram.com/..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">LinkedIn</label>
                      <input
                        onChange={handleChange}
                        value={contactData.SocialMedia.Linkedin}
                        name="Linkedin"
                        type="url"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm outline-none"
                        placeholder="https://linkedin.com/..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">GitHub</label>
                      <input
                        onChange={handleChange}
                        value={contactData.SocialMedia.GitHub}
                        name="GitHub"
                        type="url"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm outline-none"
                        placeholder="https://github.com/..."
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-3 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform active:scale-[0.99]"
                  >
                    Update Contact Details
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;